import { useState, useEffect, useRef, ReactNode } from 'react';
import { apiPublic } from '../api.js';

// Define the structure for the data payload of each result type
interface ProductData {
  id: number
  title: string;
  sku: string;
  image_url?: string;
}

interface MakeData {
  id: number
  make_name: string;
  make_slug: string;
}

interface ModelData extends MakeData {
  model_name: string;
  model_slug: string;
}

interface VehicleData extends ModelData {
  year: number;
}

interface SearchResultBase {
  result_url: string
  title: string
  relevance: number
}
interface SearchResultMake extends SearchResultBase {
  result_type: 'make'
  data: MakeData
}
interface SearchResultModel extends SearchResultBase {
  result_type: 'model'
  data: ModelData
}
interface SearchResultVehicle extends SearchResultBase {
  result_type: 'vehicle'
  data: VehicleData
}
interface SearchResultProduct extends SearchResultBase {
  result_type: 'product'
  data: ProductData
}

// Create a discriminated union for type-safe handling of different search results
type SearchResult = SearchResultMake | SearchResultModel | SearchResultVehicle | SearchResultProduct;

export function SearchBar({ ptype }: { ptype: string }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const isClearance = ptype === 'clearance';

  // Debounce effect for API calls
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      setIsLoading(true);
      let pTypeQ = "";
      if (isClearance) {
        pTypeQ = "&ptype=clearance"
      }
      apiPublic<SearchResult[]>(`/search?q=${encodeURIComponent(query)}${pTypeQ}`)
        .then(response => {
          setResults(response.data);
        })
        .catch(error => {
          console.error("Search API error:", error);
          setResults([]); // Clear results on error
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 300); // 300ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  // Effect to handle clicks outside the search bar to close the results
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchContainerRef]);

  return (
    <div className="gbs-search-container" ref={searchContainerRef}>
      <div className="gbs-search-input-wrapper">
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={isClearance ? "Search Clearance by Part, SKU, or Vehicle..." : "Search by Part, SKU, or Vehicle..."}
          className="gbs-search-input"
        />
        {isLoading && <div className="gbs-spinner"></div>}
      </div>

      {isFocused && (query.length > 1) && (
        <div className="gbs-search-results">
          {results.length > 0 ? (
            <ul>
              {results.map((result, index) => <SearchResult key={index} result={result} />)}
            </ul>
          ) : (
            !isLoading && <div className="gbs-no-results">No results found.</div>
          )}
        </div>
      )}
    </div>
  );
}


function SearchResult({ result }: { result: SearchResult }) {
  const node: ReactNode = (() => {
    switch (result.result_type) {
      case 'product':
        return <SRProduct result={result} />;
      case 'vehicle':
        return <SRVehicle result={result} />;
      case 'model':
        return <SRModel result={result} />;
      case 'make':
        return <SRMake result={result} />;
    }
  })();

  return (
  <li className={`result result-type-${result.result_type}`}>
    {node}
  </li>
  );  
}

function SRProduct({ result }: { result: SearchResultProduct }) {
  return (
    <a href={result.result_url}>
      {result.data.image_url && (
        <span className="thumbimg">
          <img src={result.data.image_url} alt={result.title} />
        </span>
      )}{" "}
      <span className="sku">{result.data.sku}</span>{" "}
      <span className="title">{result.title}</span>
    </a>
  )
}

function SRMake({ result }: { result: SearchResultMake }) {
  return (
    <a href={result.result_url}>
      <span className="name">{result.data.make_name}</span>{" "}
      <span className="desc">All {result.data.make_name} parts</span>
    </a>
  )
}

function SRModel({ result }: { result: SearchResultModel }) {
  return (
    <a href={result.result_url}>
      <span className="name">{result.data.make_name}</span>{" "}
      <span className="desc">{result.data.model_name}</span>
    </a>
  )
}

function SRVehicle({ result }: { result: SearchResultVehicle }) {
  return (
    <a href={result.result_url}>
      <span className="year">{result.data.year}</span>{" "}
      <span className="make">{result.data.make_name}</span>{" "}
      <span className="model">{result.data.model_name}</span>
    </a>
  )
}
